/**
 * 檔案用途區塊
 * @module useStrictSessionCheck
 * @purpose 在 SSO 入口以 URL tokens 推導預期鏈別，若與現有 WalletConnect session namespace 不一致則主動斷線，降低跨鏈帳號誤用風險。
 * @external https://innotech.atlassian.net/browse/IN-111892
 */
import { useEffect } from 'react';
import { useAppKitAccount, useDisconnect } from '@reown/appkit/react';
import { useSearchParams } from 'react-router-dom';
import {
  parseNamespaceFromCaipAddress,
  getTokenMapping,
} from '@modules/walletconnect/config/token-network-adapter-mapping';

/**
 * 宣告內容用途說明與單號關聯
 * @description 依 tokens 陣列判斷目前流程預期使用的 namespace，供 session 一致性檢查使用。
 * @purpose 在 SSO 載入時建立可比較的鏈別基準，避免錯誤沿用既有連線。
 * @external https://innotech.atlassian.net/browse/IN-111892
 *
 * @param tokens - Token 符號陣列（例如：['ETH', 'BTC']）
 * @returns 預期的 namespace（例如：'eip155' 或 'bip122'），如果無法判斷則返回 null
 */
function getExpectedNamespaceFromTokens(tokens: string[]): string | null {
  if (tokens.length === 0) {
    // 如果沒有 tokens，預設為 EVM
    return 'eip155';
  }

  // 檢查 tokens 中是否有 Bitcoin 相關的 token
  const hasBitcoin = tokens.some((token) => {
    const mapping = getTokenMapping(token);
    return mapping?.namespace === 'bip122';
  });

  // 檢查 tokens 中是否有 EVM 相關的 token
  const hasEVM = tokens.some((token) => {
    const mapping = getTokenMapping(token);
    return mapping?.namespace === 'eip155';
  });

  // 如果只有 Bitcoin，返回 bip122
  if (hasBitcoin && !hasEVM) {
    return 'bip122';
  }

  // 如果只有 EVM 或混合，預設為 eip155（SSO 主要使用 ETH）
  if (hasEVM) {
    return 'eip155';
  }

  // 如果無法判斷，返回 null（不執行斷線）
  return null;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 監看連線狀態與 URL tokens，檢查當前 caipAddress namespace 是否符合預期，不符即中斷連線。
 * @purpose 在 SSO 入口建立第一道 session 鏈別防線，避免跨鏈殘留連線導致登入身份錯置。
 * @external https://innotech.atlassian.net/browse/IN-111892
 *
 * @example
 * ```tsx
 * const WalletConnectLogin = () => {
 *   useStrictSessionCheck(); // 加入防線一
 *   // ... 其他邏輯
 * };
 * ```
 */
export const useStrictSessionCheck = (): void => {
  const [searchParams] = useSearchParams();
  const { isConnected, caipAddress } = useAppKitAccount();
  const { disconnect } = useDisconnect();

  useEffect(() => {
    // 如果尚未連線，不需要檢查
    if (!isConnected || !caipAddress) {
      return;
    }

    // 從 URL 參數取得 tokens
    const tokensParam = searchParams.get('tokens');
    const tokens = tokensParam
      ? tokensParam
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t.length > 0)
      : [];

    // 判斷預期的 namespace
    const expectedNamespace = getExpectedNamespaceFromTokens(tokens);

    // 如果無法判斷預期的 namespace，不執行斷線（可能是多鏈場景）
    if (!expectedNamespace) {
      return;
    }

    // 取得當前連線的 namespace
    const currentNamespace = parseNamespaceFromCaipAddress(caipAddress);

    // 如果當前 namespace 與預期不符，強制斷線
    if (currentNamespace && currentNamespace !== expectedNamespace) {
      // console.warn(`[Security] Session 衝突！預期 ${expectedNamespace} 但發現 ${currentNamespace}。執行斷線保護。`);
      void disconnect();
    }
  }, [isConnected, caipAddress, searchParams, disconnect]);
};

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:18:18.357Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Added required three-section annotation layout (top/middle/bottom), converted function and hook docs to required tags, attached Jira external link from provided declaration origin ticket IN-111892, and kept runtime logic unchanged.
 */
